<template lang="html">
  <div ref="cognitoRoot">
    <authenticator :hide-sign-up="true" :form-fields="formFields">
      <template v-slot:sign-in-header>
        <h3
          class="amplify-heading"
          style="padding: var(--amplify-space-xl) var(--amplify-space-xl) 0"
        >
          <span class="amplify-heading--3">Log In</span>

          <div v-if="isInitialSetup">
            <div class="initial-setup-notification">
              <div class="icon">
                <i class="fa fa-info-circle fa-2x"></i>
              </div>
              <div class="notice">
                We have recently upgraded the security of your account. 
                Please check your email for a new temporary password.
              </div>
              <div class="clearfix"></div>
            </div>
          </div>
        </h3>
      </template>
      <template v-slot:sign-in-footer v-if="isInitialSetup || isPasswordSetupRequired">
        <div class="sign-in-footer-container--amplify" v-if="isPasswordSetupRequired">
          If you have not received a temporary password or it has expired,
          please contact <a href="mailto:support@gofundraise.com"> support@gofundraise.com</a> 
          to send a new temporary password</div>
      </template>
      <template v-slot:confirm-sign-in-header>
        <h3
          class="amplify-heading amplify-heading--3"
        >
          Two-Factor Authentication
        </h3>
        <p>Open your Authentication App and enter the current code generated for this account</p>
      </template>
      <template v-slot="{ user, signOut }">
        <slot name="terms"></slot>
        <div>Signed in as {{ store.state.userEmail }}</div>
        not you? <a href="javascript:;" @click="signOut">Logout</a>
      </template>
      <template v-slot:reset-password-header>
        <h3 class="amplify-heading amplify-heading--3">
          Reset Password
        </h3>
        <p>Enter your email address and we will send you a verification code</p>
      </template>
      <template v-slot:confirm-reset-password-header>
        <h3 class="amplify-heading amplify-heading--3">
          Reset Password
        </h3>
        <p>Please check your email. We've sent a verification code to the email address associated with this account. Enter the code below and choose a new password.</p>
      </template>
      <template v-slot:setup-totp-header>
        <h4 class="amplify-heading amplify-heading--5">                            
          Set up two-factor authentication
        </h4>
        <div>Two factor authentication has been activated for your account. Please follow the steps to complete setup. If you need help, please contact <strong>support@gofundraise.com</strong></div>
        
        <div class="step-title-row">
          <strong>
            <span class="step-number">1</span> Install an authenticator app
          </strong>
        </div>

        <div>
          An authenticator app generates a time-based code on your device to use when you login to a platform/service. If you don't already have one, <strong>download and install an authenticator app</strong> on your mobile device such as Google Authenticator or Duo Mobile.
          <br><br>
          <div class="text-center">
            <div>Google Authenticator (<a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US" target="_blank">Android</a> | <a href="https://apps.apple.com/au/app/google-authenticator/id388497605" target="_blank">iOs</a>)</div>
            <div>Duo Mobile (<a href="https://play.google.com/store/apps/details?id=com.duosecurity.duomobile&hl=en&gl=US">Android</a> | <a href="https://apps.apple.com/us/app/duo-mobile/id422663827">iOs</a>)</div>
          </div>
        </div>

        <div class="step-title-row">
          <strong><span class="step-number">2</span> Add your GoFundraise Account</strong>
        </div>

        <div>
          Open your authentication app. Click the option to 'Add Account' or similar. <strong>Scan the QR code below in your authenticator app.</strong>
        </div>
      </template>
    </authenticator>

    <div id="setup-totp-last-step">
      <a href="javascript:;" @click="showManualKeyInfoBox" id="enter-app-key-manually">Enter key manually instead</a>
      <div id="setup-totp-qr-code-manual">
        If you are unable to scan the QR code, enter this key manually into your authenticator app:
      </div>
      <div id="setup-totp-code-input-label" class="">
        <div class="step-title-row">
          <strong>
            <span class="step-number">3</span> Enter 6-digit verification code
          </strong>
        </div>
        <div>Enter the current verification code generated by your authenticator app for the GoFundraise user account you have just setup</div>
      </div>
    </div>
    
  </div>
</template>
<script>
import { inject, ref } from 'vue'
import axios from 'axios';
import store from '../../store'
import LoginProviderInitializerBase from './LoginProviderInitializerBase.vue';
import { Authenticator,useAuthenticator, translations  } from "@aws-amplify/ui-vue";
import { Amplify, Hub, I18n } from 'aws-amplify';
import "@aws-amplify/ui-vue/styles.css";

export default {
  name: "LoginCognitoInitializer",
  components: {
    Authenticator
  },
  mixins: [ LoginProviderInitializerBase ],
  setup() {
    const setupScreenFinderInterval = ref()
    const isSetupTOPTScreen = ref(false)
    const isInitialSetup = inject('isInitialSetup')
    const isPasswordSetupRequired = inject('isPasswordSetupRequired')
    const formFields = ref({
      signIn: {
        username: {
          isRequired: true,
          label: 'Username',
          value: ''
        },
        password: {
          label: isInitialSetup.value === true || isPasswordSetupRequired.value === true
            ? 'Temporary Password'
            : 'Password',
          placeholder:  isInitialSetup.value === true || isPasswordSetupRequired.value === true ? 'Enter temporary password provided' : '',
        }
      },
      setupTOTP: {
        QR: {
          totpIssuer: 'GoFundraise',
          totpUsername: '',
        },
        confirmation_code: {
          placeholder: '6-Digit Code',
          isRequired: true,
        },
      },
      resetPassword: {
        username: {
          label: 'Email Address',
          placeholder: 'Enter your email address',
        },
      },
      confirmResetPassword: {
        confirmation_code: {
          placeholder: 'Code',
          label: 'Password Reset Code',
        },
      },
      confirmSignIn: {
        confirmation_code: {
          label: 'Authentication Code *',
          placeholder: 'Code',
          isRequired: true,
        },
      },
    })

    I18n.putVocabulariesForLanguage('en', {
      'Sign in': 'Login', 
    })

    return {
      setupScreenFinderInterval,
      isSetupTOPTScreen,
      isInitialSetup,
      isPasswordSetupRequired,
      formFields,
      store,
    }
  },
  beforeCreate() {

  },
  created() {
    var vm = this;
    vm.$gfLogger.logInfo('LoginCognitoInitializer.vue created, returnUrl: ', this.returnUrl);
    vm.$gfLogger.logInfo('LoginCognitoInitializer.vue created, providerDetails: ', this.providerDetails);

    Hub.listen('auth', (data) => {
      vm.$gfLogger.logInfo('Hub.listen auth, data:', data);
      switch (data.payload.event) {
        case 'signIn':
          this.onUserSignedIn(data.payload.data.signInUserSession.accessToken.jwtToken);
          break;
      }
    });

    I18n.putVocabularies(translations);
    I18n.putVocabularies({
      en: {
        'Password attempts exceeded': 'You have been temporarily locked out after too many password attempts. You can try again in 15 minutes. If you urgently need access, please contact support@gofundraise.com',
      },
    });
    I18n.putVocabulariesForLanguage('en', {
      'Back to Sign In': 'Sign in with a different account', 
    })

    Amplify.configure(this.providerDetails);
  },
  mounted() {
    this.$gfLogger.logInfo('LoginCognitoInitializer.vue mounted, userEmail:', this.userEmail);
    this.formFields.signIn.username.value = this.userEmail;
    this.formFields.setupTOTP.QR.totpUsername = this.userEmail;
    this.$gfLogger.logInfo('LoginCognitoInitializer.vue mounted, formFields:', this.formFields);
    this.prefillEmail(this.userEmail);
    this.checkForSetupTOTPScreen()
  },
  beforeUnmount() {
    this.$gfLogger.logInfo('LoginCognitoInitializer.vue beforeDestroy');
  },
  methods: {
    showManualKeyInfoBox() {
      var qrCopy = document.querySelector('[data-amplify-authenticator-setup-totp] [data-amplify-copy]')
      qrCopy.setAttribute('style', 'display: block !important;')

      var qrcodeManualDiv = document.querySelector('#setup-totp-qr-code-manual')
      qrcodeManualDiv.setAttribute('style', 'display: block !important;')

      qrCopy.prepend(qrcodeManualDiv)
      document.querySelector('#enter-app-key-manually')
        .setAttribute('style', 'display: none !important;')
    },
    checkForSetupTOTPScreen() {
      this.setupScreenFinderInterval = setInterval(() => {
        var qrCopy = document.querySelector('[data-amplify-authenticator-setup-totp] [data-amplify-copy]')
        var qrCode = document.querySelector('[data-amplify-authenticator-setup-totp] [data-amplify-qrcode]')
        if (qrCode && qrCopy) {
          clearInterval(this.setupScreenFinderInterval)

          var lastStepDiv = document.querySelector('#setup-totp-last-step')
          lastStepDiv.setAttribute('style', 'display: block;')
          
          qrCopy.parentNode.insertBefore(
            lastStepDiv,
            qrCopy.nextSibling
          )

          var inputLabel = document.querySelector('#setup-totp-code-input-label')
          var qrLabel = document.querySelector('[data-amplify-authenticator-setup-totp] .amplify-authenticator__column .amplify-label')

          inputLabel.setAttribute('style', 'display: block;')
          qrLabel.innerHTML = ''
          qrLabel.prepend(inputLabel)
        }
      }, 500)
    },
    prefillEmail(email) {
      var vm = this
      setTimeout(() => {
        if (document.querySelector('[name="username"]')) {
          document.querySelector('[name="username"]').value = email;
          document.querySelector('[name="username"]').setAttribute('readonly', true)
          document.querySelector('[name="username"]').setAttribute('style', 'background: #EEE; border: 1px solid #EEE; border-top-left-radius: 3px; border-bottom-left-radius: 3px;')
          const outerEnd = document.createElement('div')
          outerEnd.setAttribute('class', 'amplify-field-group__outer-end')
          outerEnd.innerHTML = '<button aria-label="Edit Username" id="change-username-field--amplify" aria-checked="false" class="amplify-button amplify-field-group__control" data-fullwidth="false" type="button" role="switch"><i class="fa fa-edit fa-fw"></i></button>'
          document.querySelector('[name="username"]').parentNode.parentNode.appendChild(outerEnd)
          document.getElementById('change-username-field--amplify').addEventListener('click', () => {
            vm.$emit('requestChangeUsername')
          })
        }
      }, 50);
    },
    onUserSignedIn(jwtAccessToken) {
      var vm = this;
      vm.$gfLogger.logInfo('LoginCognitoInitializer.vue onUserSignedIn, jwtAccessToken:', jwtAccessToken);

      vm.processToken(jwtAccessToken)
    },
    processToken(jwtToken) {
      var vm = this;
      vm.$gfLogger.logInfo('processToken.start', jwtToken); 

      axios
        .post("/oauth/process-token/cognito", { token: jwtToken }, {
            headers: {
              "content-type": "application/json;charset=utf-8"
            }
          })
        .then(function(processTokenResult) {
          vm.$gfLogger.logInfo('/oauth/process-token/cognito success', processTokenResult);                         
          var returnURL = processTokenResult.data.ValidToken === true                        
            ? vm.returnUrl
            : '/account/logon';

          if (vm.isInitialSetup === true) {
            vm.isInitialSetup = false
          }
          
          useAuthenticator().signOut();

          vm.$gfLogger.logInfo('processToken /oauth/process-token/cognito.user', store.state.userEmail)

          vm.$emit("providerSuccessLogin", {
            returnURL,
            userName: store.state.userEmail
          })
        })
        .catch(function(res) {
            vm.$gfLogger.logInfo('processTokenResult failed', res);
            vm.$emit("providerFailedLogin")
        });

    }
  }
};
</script>
<style>
.initial-setup-notification {
  display: flex;
  margin-top: 15px;
  background: var(--amplify-colors-teal-10); 
  padding: 10px; 
  border: 1px solid var(--amplify-colors-teal-40); 
  border-radius: var(--amplify-radii-medium);
}
.initial-setup-notification .icon {
  float: left; 
  margin-right: 5px;
}
.initial-setup-notification .notice {
  padding: 0px 5px;
}
.initial-setup-notification .clearfix {
  clear: both;
}
.sign-in-footer-container--amplify {
  text-align: center;
  display: block;
}
.amplify-button[data-variation='primary'] {
    display: inline-block;
    font-weight: 400;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    border: 1px solid transparent;
    padding: .375rem .75rem;
    font-size: 1rem;
    line-height: 1.5;
    border-radius: .25rem;
    transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out;
    display: block;
    width: 100%;
    background: #f47d32 !important;
    color: #fff !important;
}
[data-amplify-authenticator] [data-amplify-router] {
    background-color: none;
    box-shadow: none;
    border-width: 0px;
    border-style: none;
}

[data-amplify-copy], [data-amplify-copy-svg] {
  display: none !important;
}

.text-center {
  text-align: center;
}

.step-title-row {
  margin-top: 20px;
}

#setup-totp-code-input-label .step-title-row {
  color: black;
  margin-bottom: 15px;
}

.step-number {
  border: 1px solid var(--amplify-colors-blue-60);
  border-radius: 20px;
  padding: 5px 10px;
  color: var(--amplify-colors-blue-60);
  margin-right: 6px;
}

#change-username-field--amplify {
  border: 1px solid #EEE;
}

#setup-totp-last-step {
  display: none;
  margin: -20px 5px 20px; 
  text-align: center;
}

#setup-totp-qr-code-manual {
  margin-bottom: 5px;
  display: none;
}

#setup-totp-code-input-label {
  display: none;
}
</style>